import { useMemo, useEffect, useState, useCallback } from "react";
import { useOldMemberStatus } from "@/hooks/useLegacyNotes";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import EnvironmentBanner from "@/components/EnvironmentBanner";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDeviceMode } from "@/hooks/useDeviceMode";
import MobileAppShell from "@/components/layouts/MobileAppShell";
import LiveChatWidget from "@/components/support/LiveChatWidget";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import { useAuth } from "@/hooks/useAuth";
import { useAuthState } from "@/hooks/useAuthState";
import { useBranding } from "@/hooks/useBranding";
import BrandLogo from "@/components/BrandLogo";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useUserProfile } from "@/hooks/useUserProfile";
import {
  LayoutDashboard, Shield, Settings, LogOut, ChevronDown, Bell,
  LogIn, UserPlus,
  ShieldCheck, LifeBuoy, Code, Users,
  ChevronRight, Menu, PanelLeftOpen, PanelLeftClose,
  Wallet, ClipboardList, UserCircle, Link2,
} from "lucide-react";
import LandingDesktopNav from "@/components/landing/LandingDesktopNav";
import NotificationBell from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNotesMarketStatus } from "@/hooks/useNotesMarketStatus";
import { useCardsStatus } from "@/hooks/useCardsStatus";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import ContextualHelpPanel from "@/components/ContextualHelpPanel";
import BrowserNotificationPrompt from "@/components/BrowserNotificationPrompt";
import SoundUnlockPrompt from "@/components/SoundUnlockPrompt";

import { useNotificationListener } from "@/hooks/useNotificationListener";
import { sidebarGroups, isSidebarItemActive, type SidebarItem, type SidebarGroupDef } from "@/config/sidebarConfig";

const FULL_WIDTH_PATTERNS = ["/app/spot", "/app/futures", "/app/p2p", "/app/p2p/express", "/app/p2p/block", "/trade/", "/app/binary"];

/** Pages that handle help inline — suppress fixed side panel + right padding */
const INLINE_HELP_PAGES = [
  "/app/dashboard",
  "/app/wallets",
  "/app/exchange",
  "/app/crypto/withdraw",
  "/app/crypto/deposit",
];

/** Map route prefixes to FAQ context keys */
const ROUTE_FAQ_MAP: { pattern: string; module: string; page?: string }[] = [
  { pattern: "/app/dashboard", module: "dashboard", page: "overview" },
  { pattern: "/app/wallets", module: "wallets", page: "list" },
  { pattern: "/app/crypto/deposit", module: "wallets", page: "deposit" },
  { pattern: "/app/crypto/withdraw", module: "wallets", page: "withdraw" },
  { pattern: "/app/fiat/deposit", module: "wallets", page: "deposit" },
  { pattern: "/app/fiat/withdraw", module: "wallets", page: "withdraw" },
  { pattern: "/app/exchange", module: "wallets", page: "exchange" },
  { pattern: "/app/staking/subscribe", module: "staking", page: "subscribe" },
  { pattern: "/app/staking/positions", module: "staking", page: "positions" },
  { pattern: "/app/staking", module: "staking", page: "list" },
  { pattern: "/app/my-stakings", module: "staking", page: "positions" },
  { pattern: "/app/notes-market/sell", module: "notes_market", page: "sell" },
  { pattern: "/app/notes-market/history", module: "notes_market", page: "history" },
  { pattern: "/app/notes-market/", module: "notes_market", page: "purchase" },
  { pattern: "/app/notes-market", module: "notes_market", page: "list" },
  { pattern: "/app/my-notes/", module: "my_notes", page: "detail" },
  { pattern: "/app/my-notes", module: "my_notes", page: "list" },
  { pattern: "/app/token-sale/portfolio", module: "token_sale", page: "list" },
  { pattern: "/app/token-sale/", module: "token_sale", page: "purchase" },
  { pattern: "/app/token-sale", module: "token_sale", page: "list" },
  { pattern: "/app/kyc", module: "kyc", page: "submit" },
  { pattern: "/app/security", module: "security", page: "overview" },
  { pattern: "/app/support", module: "support", page: "tickets" },
  { pattern: "/app/referrals", module: "referrals", page: "overview" },
  { pattern: "/app/copy-trading", module: "copy_trading", page: "overview" },
  { pattern: "/app/earn", module: "earn", page: "list" },
  { pattern: "/app/futures", module: "futures", page: "overview" },
  { pattern: "/app/spot", module: "spot", page: "overview" },
  { pattern: "/app/p2p", module: "p2p", page: "market" },
  { pattern: "/app/virtual-cards", module: "virtual_cards", page: "list" },
  { pattern: "/app/developer", module: "developer", page: "overview" },
  { pattern: "/app/team", module: "team", page: "overview" },
  { pattern: "/app/ambassador", module: "ambassador", page: "overview" },
  { pattern: "/app/settings", module: "settings", page: "overview" },
  { pattern: "/app/notifications", module: "notifications", page: "list" },
  { pattern: "/app/help", module: "help_center", page: "list" },
];

function resolveFaqContext(pathname: string) {
  for (const entry of ROUTE_FAQ_MAP) {
    if (pathname === entry.pattern || pathname.startsWith(entry.pattern + "/") || (entry.pattern.endsWith("/") && pathname.startsWith(entry.pattern))) {
      return { moduleKey: entry.module, pageKey: entry.page };
    }
  }
  return null;
}

export default function UserLayout() {
  const { isMobile } = useDeviceMode();
  const { isAuthed, isInitializing } = useAuthState();
  const { pathname } = useLocation();

  // Global notification listener — toast, sound, browser push
  useNotificationListener();

  // The P2P order screen has its own in-page chat and help actions — the
  // floating support bubble would sit on top of them.
  const hideSupportFab = /^\/app\/p2p\/orders\/[^/]+/.test(pathname);

  // While auth is still resolving on mobile, render the mobile shell so we
  // don't briefly flash the desktop guest layout. ProtectedRoute already
  // gates access to private pages.
  if (isMobile && (isAuthed || isInitializing)) {
    return (
      <>
        <MobileAppShell />
        {!hideSupportFab && <LiveChatWidget />}
        <BrowserNotificationPrompt />
        <SoundUnlockPrompt />
      </>
    );
  }

  return (
    <>
      <DesktopLayout />
      {isAuthed && !hideSupportFab && <LiveChatWidget />}
      {isAuthed && <BrowserNotificationPrompt />}
      {isAuthed && <SoundUnlockPrompt />}
    </>


  );
}

function DesktopLayout() {
  const { locked: notesLocked } = useNotesMarketStatus();
  const { locked: cardsLocked } = useCardsStatus();
  const { user, signOut } = useAuth();
  const { isAuthed, isInitializing, isGuest } = useAuthState();
  const { appName } = useBranding();
  usePlatformSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const { data: oldMemberData } = useOldMemberStatus();
  const isOldMember = oldMemberData?.is_old_member || false;
  const [collapsed, setCollapsed] = useState(false);

  const isFullWidthPage = FULL_WIDTH_PATTERNS.some((p) => location.pathname.startsWith(p));
  const isInlineHelpPage = INLINE_HELP_PAGES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const faqContext = !isFullWidthPage && !isInlineHelpPage ? resolveFaqContext(location.pathname) : null;

  const filterItem = useCallback((i: SidebarItem): SidebarItem | null => {
    if (i.moduleKey && !isModuleEnabled(i.moduleKey)) return null;
    if (i.oldMemberOnly && !isOldMember) return null;
    if (i.children) {
      const filtered = i.children.map(filterItem).filter(Boolean) as SidebarItem[];
      return { ...i, children: filtered };
    }
    return i;
  }, [isModuleEnabled, isOldMember]);

  const filteredGroups = useMemo(
    () =>
      sidebarGroups
        .map((g) => ({
          ...g,
          items: g.items.map(filterItem).filter(Boolean) as SidebarItem[],
        }))
        .filter((g) => g.items.length > 0),
    [filterItem]
  );

  // Track which parent items (children) are expanded
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => new Set());
  // Track which top-level groups are collapsed
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(sidebarGroups.map(g => g.key))
  );

  // Auto-expand parents + groups whose children match current route (replace, not merge)
  useEffect(() => {
    const nextParents = new Set<string>();
    let activeGroupKey = "";
    for (const g of sidebarGroups) {
      for (const item of g.items) {
        const childActive = !!item.children?.some((c) =>
          isSidebarItemActive(c.to, location.pathname, location.search)
        );
        const selfActive = isSidebarItemActive(item.to, location.pathname, location.search);
        if (selfActive || childActive) {
          activeGroupKey = g.key;
        }
        if (item.children && (selfActive || childActive)) {
          nextParents.add(item.to);
        }
      }
    }
    // Replace: only the active parent is expanded
    setExpandedParents(nextParents);
    // Replace: collapse all groups except the active one
    setCollapsedGroups(
      new Set(sidebarGroups.map(g => g.key).filter(k => k !== activeGroupKey))
    );
  }, [location.pathname, location.search]);

  const toggleParent = (parentTo: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentTo)) next.delete(parentTo);
      else next.add(parentTo);
      return next;
    });
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      if (prev.has(groupKey)) {
        const allKeys = sidebarGroups.map(g => g.key);
        return new Set(allKeys.filter(k => k !== groupKey));
      } else {
        return new Set([...prev, groupKey]);
      }
    });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const { avatarUrl, maskedEmail, initials: userInitials, displayName: userName, verificationTier } = useUserProfile();

  const loginRedirectUrl = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
  const signupRedirectUrl = `/signup?redirect=${encodeURIComponent(location.pathname + location.search)}`;

  const showSidebar = isAuthed && !isFullWidthPage;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <ImpersonationBanner />
      <EnvironmentBanner />
      {/* ── Top Navbar ── */}
      <header className="fixed left-0 right-0 z-50 glass-strong border-b border-border/50 h-16 flex items-center justify-between px-4 lg:px-6 xl:px-8" style={{ top: "calc(var(--env-banner-height, 0px) + var(--impersonation-banner-height, 0px))" }}>
        <div className="flex items-center gap-0.5 lg:gap-1">
          {isAuthed && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex mr-1"
              onClick={() => setCollapsed((c) => !c)}
            >
              {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>
          )}
          <Link to={isAuthed ? "/app/dashboard" : "/"} className="mr-4 shrink-0">
            <BrandLogo className="h-8 max-w-[160px] object-contain" />
          </Link>

          {isAuthed && (
            <Link
              to="/app/dashboard"
              className={cn(
                "hidden lg:inline-flex px-2 xl:px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                location.pathname === "/app/dashboard"
                  ? "text-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Dashboard
            </Link>
          )}

          <div className="hidden lg:flex">
            <LandingDesktopNav />
          </div>
        </div>

        <div className="flex items-center gap-1">
          <ThemeToggle />
          {isInitializing ? (
            // Neutral spacer during auth init — prevents Login/Sign Up flashing
            // for authenticated users on hard refresh / slow networks.
            <div className="w-[180px] h-9" aria-hidden />
          ) : isGuest ? (
            <>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(loginRedirectUrl)}>
                <LogIn className="h-4 w-4" /> Log In
              </Button>
              <Button variant="default" size="sm" className="gap-1.5" onClick={() => navigate(signupRedirectUrl)}>
                <UserPlus className="h-4 w-4" /> Sign Up
              </Button>
            </>
          ) : (
            <>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <Avatar className="h-8 w-8 border border-primary/30">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt="Avatar" />}
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">{userInitials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium text-foreground">{userName}</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-0">
                  {/* Profile header */}
                  <div className="p-4 flex items-start gap-3 border-b border-border/50">
                    <Avatar className="h-10 w-10 border border-primary/30 shrink-0">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt="Avatar" />}
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">{userInitials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">{maskedEmail}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">ID: {user?.id?.substring(0, 8)}</p>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5">Regular User</Badge>
                        {verificationTier > 0 ? (
                          <Badge className="text-[10px] h-5 px-1.5 bg-emerald-500/20 text-emerald-500 border-emerald-500/30">Verified</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-muted-foreground">Unverified</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation items */}
                  <div className="py-1">
                    <DropdownMenuItem onClick={() => navigate("/app/dashboard")} className="px-4 py-2.5 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span className="flex-1 text-sm">Dashboard</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/app/wallets")} className="px-4 py-2.5 cursor-pointer">
                      <Wallet className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span className="flex-1 text-sm">Assets</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/app/orders")} className="px-4 py-2.5 cursor-pointer">
                      <ClipboardList className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span className="flex-1 text-sm">Orders</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </DropdownMenuItem>
                  </div>

                  <DropdownMenuSeparator className="my-0" />

                  {/* Account section */}
                  <div className="py-1">
                    <DropdownMenuLabel className="px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Account</DropdownMenuLabel>
                    {isModuleEnabled("kyc_verification") && (
                      <DropdownMenuItem onClick={() => navigate("/app/kyc")} className="px-4 py-2.5 cursor-pointer">
                        <ShieldCheck className="h-4 w-4 mr-3 text-muted-foreground" />
                        <span className="flex-1 text-sm">Verification</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/app/security")} className="px-4 py-2.5 cursor-pointer">
                      <Shield className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span className="flex-1 text-sm">Security</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/app/settings")} className="px-4 py-2.5 cursor-pointer">
                      <Settings className="h-4 w-4 mr-3 text-muted-foreground" />
                      <span className="flex-1 text-sm">Settings</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                    </DropdownMenuItem>
                    {isModuleEnabled("support_tickets") && (
                      <DropdownMenuItem onClick={() => navigate("/app/support")} className="px-4 py-2.5 cursor-pointer">
                        <LifeBuoy className="h-4 w-4 mr-3 text-muted-foreground" />
                        <span className="flex-1 text-sm">Support</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                      </DropdownMenuItem>
                    )}
                    {isModuleEnabled("referrals") && (
                      <DropdownMenuItem onClick={() => navigate("/app/referrals")} className="px-4 py-2.5 cursor-pointer">
                        <Link2 className="h-4 w-4 mr-3 text-muted-foreground" />
                        <span className="flex-1 text-sm">Referral</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                      </DropdownMenuItem>
                    )}
                  </div>

                  {(isModuleEnabled("developer") || isModuleEnabled("team")) && (
                    <>
                      <DropdownMenuSeparator className="my-0" />
                      <div className="py-1">
                        <DropdownMenuLabel className="px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Tools</DropdownMenuLabel>
                        {isModuleEnabled("developer") && (
                          <DropdownMenuItem onClick={() => navigate("/app/developer")} className="px-4 py-2.5 cursor-pointer">
                            <Code className="h-4 w-4 mr-3 text-muted-foreground" />
                            <span className="flex-1 text-sm">Developer</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                          </DropdownMenuItem>
                        )}
                        {isModuleEnabled("team") && (
                          <DropdownMenuItem onClick={() => navigate("/app/team")} className="px-4 py-2.5 cursor-pointer">
                            <Users className="h-4 w-4 mr-3 text-muted-foreground" />
                            <span className="flex-1 text-sm">Team</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                          </DropdownMenuItem>
                        )}
                      </div>
                    </>
                  )}

                  <DropdownMenuSeparator className="my-0" />
                  <div className="py-1">
                    <DropdownMenuItem onClick={handleSignOut} className="px-4 py-2.5 text-destructive cursor-pointer">
                      <LogOut className="h-4 w-4 mr-3" />
                      <span className="text-sm">Sign Out</span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </header>

      {/* ── Body: Sidebar + Content ── */}
      <div className="flex flex-1 min-h-0" style={{ paddingTop: "calc(4rem + var(--env-banner-height, 0px) + var(--impersonation-banner-height, 0px))" }}>
        {showSidebar && (
          <>
            <aside
              className={cn(
                "hidden lg:flex flex-col border-r border-border/50 bg-background shrink-0 fixed left-0 transition-all duration-200 z-30",
                collapsed ? "w-16" : "w-[248px]"
              )}
              style={{
                top: "calc(4rem + var(--env-banner-height, 0px) + var(--impersonation-banner-height, 0px))",
                height: "calc(100vh - 4rem - var(--env-banner-height, 0px) - var(--impersonation-banner-height, 0px))",
              }}
            >
              <TooltipProvider delayDuration={300}>
              <div className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
                {/* Standalone Dashboard link */}
                {collapsed ? (
                  <div className="mb-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to="/app/dashboard"
                          className={cn(
                            "flex justify-center py-2.5 rounded-lg transition-colors",
                            location.pathname === "/app/dashboard" ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                          )}
                        >
                          <LayoutDashboard className="h-5 w-5" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right">Dashboard</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <Link
                    to="/app/dashboard"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 mb-1 rounded-lg text-sm font-medium transition-colors",
                      location.pathname === "/app/dashboard"
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                    )}
                  >
                    <LayoutDashboard className="h-4 w-4 shrink-0" />
                    <span>Dashboard</span>
                  </Link>
                )}

                {modulesLoading ? (
                  <div className="space-y-3 px-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="h-3 w-16 rounded bg-muted animate-pulse" />
                        <div className="h-8 w-full rounded-lg bg-muted animate-pulse" />
                        <div className="h-8 w-full rounded-lg bg-muted animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : filteredGroups.map((group) => {
                  const isGroupCollapsed = collapsedGroups.has(group.key);
                  const GroupIcon = group.icon;
                  const isGroupActive = group.items.some((item) =>
                    isSidebarItemActive(item.to, location.pathname, location.search) ||
                    (item.children && item.children.some((c) => isSidebarItemActive(c.to, location.pathname, location.search)))
                  );

                  return (
                    <div key={group.key}>
                      {/* Group header — collapsible toggle */}
                      {!collapsed ? (
                        <button
                          onClick={() => toggleGroup(group.key)}
                          className="w-full flex items-center justify-between px-3 py-1.5 mb-0.5 rounded-md hover:bg-accent/40 transition-colors group"
                        >
                          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            <GroupIcon className="h-3.5 w-3.5" />
                            {group.label}
                          </span>
                          <ChevronDown
                            className={cn(
                              "h-3 w-3 text-muted-foreground/60 transition-transform duration-200",
                              isGroupCollapsed && "-rotate-90"
                            )}
                          />
                        </button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={group.items[0]?.to || "#"}
                              className={cn(
                                "flex justify-center py-2.5 rounded-lg transition-colors",
                                isGroupActive ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                              )}
                            >
                              <GroupIcon className="h-5 w-5" />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right">{group.label}</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Group items — hidden when group collapsed or sidebar collapsed */}
                      {(!isGroupCollapsed && !collapsed) && (
                        <div className={cn("mb-3", !collapsed && "relative ml-[14px] py-1")}>
                          {group.items.map((item, itemIdx) => {
                            const hasChildren = item.children && item.children.length > 0;
                            const isParentActive = isSidebarItemActive(item.to, location.pathname, location.search);
                            const isExpanded = expandedParents.has(item.to);
                            const isLastItem = itemIdx === group.items.length - 1;

                            if (hasChildren) {
                              return (
                                <div key={item.to} className="space-y-0.5">
                                  <div
                                    className={cn(
                                      "flex items-center rounded-lg py-2 text-sm font-medium transition-colors cursor-pointer",
                                      !collapsed
                                        ? cn(
                                            "relative pl-6 pr-3",
                                        "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                                            !isLastItem && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                                          )
                                        : "px-3 justify-center",
                                      isParentActive
                                        ? "bg-primary/10 text-primary ml-1 rounded-lg"
                                        : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                                    )}
                                    title={collapsed ? item.label : undefined}
                                  >
                                    <Link
                                      to={item.to}
                                      className="flex items-center gap-3 flex-1 min-w-0"
                                      onClick={() => setExpandedParents(new Set([item.to]))}
                                    >
                                      <item.icon className="h-4 w-4 shrink-0" />
                                      {!collapsed && <span className="whitespace-normal break-words leading-snug text-left min-w-0">{item.label}</span>}
                                    </Link>
                                    {!collapsed && (
                                      <button
                                        type="button"
                                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                                        aria-expanded={isExpanded}
                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleParent(item.to); }}
                                        className="p-2 -mr-1 rounded-md hover:bg-accent/60 transition-colors"
                                      >
                                       <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200", isExpanded && "rotate-90")} />
                                      </button>
                                    )}
                                  </div>

                                  {!collapsed && isExpanded && (
                                    <div className="relative ml-[22px] py-1">
                                      {item.children!.map((child, idx) => {
                                        const childActive = isSidebarItemActive(child.to, location.pathname, location.search);
                                        const isLast = idx === item.children!.length - 1;
                                        return (
                                          <Link
                                            key={child.to}
                                            to={child.to}
                                            className={cn(
                                              "relative flex items-center pl-6 pr-3 py-1.5 text-[13px] font-medium transition-colors",
                                              "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                                              childActive
                                                ? "text-primary font-semibold bg-primary/5 rounded-lg ml-1"
                                                : "text-muted-foreground hover:text-foreground",
                                              !isLast && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                                            )}
                                          >
                                            <span className="whitespace-normal break-words leading-snug text-left min-w-0">{child.label}</span>
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // Regular flat item with tree connector
                            const active = isSidebarItemActive(item.to, location.pathname, location.search);
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                className={cn(
                                  "flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors",
                                  !collapsed
                                    ? cn(
                                        "relative pl-6 pr-3",
                                        "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                                        !isLastItem && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                                      )
                                    : "px-3 justify-center",
                                  active
                                    ? "bg-primary/10 text-primary ml-1 rounded-lg"
                                    : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                                )}
                                title={collapsed ? item.label : undefined}
                              >
                                <item.icon className="h-4 w-4 shrink-0" />
                                {!collapsed && (
                                  <span className="flex items-center gap-1.5">
                                    {item.label}
                                    {((notesLocked && item.to.includes("notes")) ||
                                      (cardsLocked && item.to.includes("cards"))) && (
                                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                        Soon
                                      </span>
                                    )}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              </TooltipProvider>

              <button
                onClick={() => setCollapsed((c) => !c)}
                className="flex items-center justify-center h-10 border-t border-border/50 text-muted-foreground hover:text-foreground transition-colors"
              >
                {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              </button>
            </aside>
            <div className={cn("hidden lg:block shrink-0 transition-all duration-200", collapsed ? "w-16" : "w-[248px]")} />
          </>
        )}

        {/* Main Content */}
        <main className={cn("flex-1 overflow-y-auto pb-20 lg:pb-12 [&>.spot-terminal-page]:p-0 [&>.futures-terminal-page]:p-0 p-4 lg:p-8 content-glow-bg relative z-10", faqContext && "xl:pr-[24rem]")}>
          <Outlet />
          {faqContext && (
            <ContextualHelpPanel
              moduleKey={faqContext.moduleKey}
              pageKey={faqContext.pageKey}
            />
          )}
        </main>
      </div>
    </div>
  );
}
